import * as React from 'react'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import {
  aiAgentsPath,
  aiCronPath,
  aiDashboardPath,
  aiDocumentationPath,
  aiKeysPath,
  aiLogsPath,
  aiModelsPath,
  aiPluginsPath,
  aiSessionsPath,
  aiSkillsPath,
  equabotzSettingsPath,
  primePath,
} from '@logic'
import { screenSizes } from '@src/styles/theme'

type LinkDef = {
  to: string
  label: string
  priority: 'p1' | 'p2' | 'p3'
  exact?: boolean
}

const LINKS: ReadonlyArray<LinkDef> = [
  { to: primePath, label: 'Chat', priority: 'p1', exact: true },
  { to: aiSessionsPath, label: 'Sessions', priority: 'p1' },
  { to: aiDashboardPath, label: 'Analytics', priority: 'p1' },
  { to: aiModelsPath, label: 'Models', priority: 'p1' },
  { to: aiLogsPath, label: 'Logs', priority: 'p1' },
  { to: aiCronPath, label: 'Cron', priority: 'p1' },
  { to: aiSkillsPath, label: 'Skills', priority: 'p2' },
  { to: aiPluginsPath, label: 'Plugins', priority: 'p2' },
  { to: aiAgentsPath, label: 'Profiles · Multi Agents', priority: 'p2' },
  { to: equabotzSettingsPath, label: 'Config', priority: 'p2' },
  { to: aiKeysPath, label: 'Keys', priority: 'p3' },
  { to: aiDocumentationPath, label: 'Documentation', priority: 'p3' },
]

const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 12px;
  background-color: ${(p) => p.theme.modalBack};
  border-radius: ${(p) => p.theme.radius};
  border: 1px solid ${(p) => p.theme.input.default};
  margin-bottom: 16px;
  align-items: center;

  @media (max-width: ${screenSizes.XS}px) {
    overflow-x: auto;
    flex-wrap: nowrap;
  }
`

const NavItem = styled<any>(NavLink)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: ${(p) => p.theme.radius};
  font-size: 13px;
  font-family: 'NunitoSansSemiBold';
  color: ${(p) => p.theme.white};
  text-decoration: none;
  min-height: 36px;
  white-space: nowrap;

  &:hover {
    background-color: ${(p) => p.theme.input.default};
  }

  &.active {
    background-color: ${(p) => p.theme.button.primary.default};
    color: ${(p) => p.theme.white};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${(p) => p.theme.white}, 0 0 0 4px ${(p) => p.theme.accent};
  }
`

export type AdminNavProps = {
  /** Rendered inside each nav item when provided (e.g. the Approvals count badge). */
  renderBadge?: (to: string) => React.ReactNode
}

/**
 * AdminNav — persistent sub-navigation for every `/ai/*` admin page (FR-001).
 * Keyboard reachable and labelled per NFR-003.
 */
export function AdminNav({ renderBadge }: AdminNavProps) {
  return (
    <Nav aria-label="Equabot admin sections">
      {LINKS.map((link) => (
        <NavItem
          key={link.to}
          to={link.to}
          exact={link.exact}
          activeClassName="active"
          data-priority={link.priority}
        >
          <span>{link.label}</span>
          {renderBadge ? renderBadge(link.to) : null}
        </NavItem>
      ))}
    </Nav>
  )
}
